"use strict";
/*
 * Copyright (C) 2017-2019 HERE Europe B.V.
 * Licensed under Apache 2.0, see full license in LICENSE
 * SPDX-License-Identifier: Apache-2.0
 */
Object.defineProperty(exports, "__esModule", { value: true });
const harp_datasource_protocol_1 = require("@here/harp-datasource-protocol");
const THREE = require("three");
const MapViewPoints_1 = require("./MapViewPoints");
const PickingRaycaster_1 = require("./PickingRaycaster");
const RoadPicker_1 = require("./RoadPicker");
/**
 * Describes the general type of a picked object.
 */
var PickObjectType;
(function (PickObjectType) {
    /**
     * Unspecified.
     */
    PickObjectType[PickObjectType["Unspecified"] = 0] = "Unspecified";
    /**
     * A point object.
     */
    PickObjectType[PickObjectType["Point"] = 1] = "Point";
    /**
     * A line object.
     */
    PickObjectType[PickObjectType["Line"] = 2] = "Line";
    /**
     * An area object.
     */
    PickObjectType[PickObjectType["Area"] = 3] = "Area";
    /**
     * The text part of a [[TextElement]]
     */
    PickObjectType[PickObjectType["Text"] = 4] = "Text";
    /**
     * The Icon of a [[TextElement]].
     */
    PickObjectType[PickObjectType["Icon"] = 5] = "Icon";
    /**
     * Any general 3D object, for example, a landmark.
     */
    PickObjectType[PickObjectType["Object3D"] = 6] = "Object3D";
})(PickObjectType = exports.PickObjectType || (exports.PickObjectType = {}));
/**
 * Handles the picking of scene geometry and roads.
 */
class PickHandler {
    constructor(mapView, enableRoadPicking = true) {
        this.mapView = mapView;
        this.enableRoadPicking = enableRoadPicking;
        this.m_plane = new THREE.Plane(new THREE.Vector3(0, 0, 1));
        this.m_rayCaster = new PickingRaycaster_1.PickingRaycaster(mapView);
        if (enableRoadPicking) {
            this.m_roadPicker = new RoadPicker_1.RoadPicker(mapView);
        }
    }
    /**
     * The `RoadPicker` class manages picking of roads, which may not be pickable in THREE.js,
     * since their geometry is generated in the vertex shader. The `RoadPicker` requires that
     * all [[Tile]]s are registered before they can be picked successfully.
     */
    registerTile(tile) {
        return this.m_roadPicker !== undefined ? this.m_roadPicker.registerTile(tile) : undefined;
    }
    /**
     * Does a raycast on all objects in the scene; useful for picking. This function is Limited to
     * objects that THREE.js can raycast. However, any solid lines that have their geometry in the
     * shader cannot be tested for intersection.
     *
     * @param x The X position in CSS/client coordinates, without the applied display ratio.
     * @param y The Y position in CSS/client coordinates, without the applied display ratio.
     * @returns the list of intersection results.
     */
    intersectMapObjects(x, y) {
        const worldPos = this.mapView.getNormalizedScreenCoordinates(x, y);
        const rayCaster = this.m_rayCaster;
        const pickResults = [];
        if (this.mapView.textElementsRenderer !== undefined) {
            const { clientWidth, clientHeight } = this.mapView.canvas;
            const screenX = worldPos.x * clientWidth * 0.5 * this.mapView.pixelRatio;
            const screenY = worldPos.y * clientHeight * 0.5 * this.mapView.pixelRatio;
            const scenePosition = new THREE.Vector2(screenX, screenY);
            this.mapView.textElementsRenderer.pickTextElements(scenePosition, pickResults);
        }
        rayCaster.setFromCamera(worldPos, this.mapView.camera);
        // calculate objects intersecting the picking ray
        const intersects = rayCaster.intersectObjects(this.mapView.worldRootObject.children, true);
        for (const intersect of intersects) {
            const pickResult = {
                type: PickObjectType.Unspecified,
                point: intersect.point,
                distance: intersect.distance,
                intersection: intersect
            };
            if (intersect.object.userData !== undefined) {
                const featureData = intersect.object.userData !== undefined
                    ? intersect.object.userData.feature
                    : undefined;
                if (featureData === undefined) {
                    pickResults.push(pickResult);
                    continue;
                }
                this.addObjInfo(featureData, intersect, pickResult);
                if (featureData.ids !== undefined) {
                    const featureId = featureData.ids.length === 1 ? featureData.ids[0] : undefined;
                    pickResult.featureId = featureId;
                }
                let pickObjectType;
                switch (featureData.geometryType) {
                    case harp_datasource_protocol_1.GeometryType.Point:
                    case harp_datasource_protocol_1.GeometryType.Text:
                        pickObjectType = PickObjectType.Point;
                        break;
                    case harp_datasource_protocol_1.GeometryType.Line:
                    case harp_datasource_protocol_1.GeometryType.ExtrudedLine:
                    case harp_datasource_protocol_1.GeometryType.SolidLine:
                    case harp_datasource_protocol_1.GeometryType.TextPath:
                        pickObjectType = PickObjectType.Line;
                        break;
                    case harp_datasource_protocol_1.GeometryType.Polygon:
                    case harp_datasource_protocol_1.GeometryType.ExtrudedPolygon:
                        pickObjectType = PickObjectType.Area;
                        break;
                    case harp_datasource_protocol_1.GeometryType.Object3D:
                        pickObjectType = PickObjectType.Object3D;
                        break;
                    default:
                        pickObjectType = PickObjectType.Unspecified;
                }
                pickResult.type = pickObjectType;
                pickResults.push(pickResult);
            }
        }
        if (this.enableRoadPicking) {
            const planeIntersectPosition = new THREE.Vector3();
            rayCaster.ray.intersectPlane(this.m_plane, planeIntersectPosition);
            planeIntersectPosition.add(this.mapView.worldCenter);
            const cameraPos = this.mapView.worldCenter.clone().add(this.mapView.camera.position);
            this.mapView.forEachVisibleTile(tile => {
                this.m_roadPicker.intersectRoads(tile, cameraPos, planeIntersectPosition, pickResults);
            });
        }
        pickResults.sort((a, b) => {
            return a.distance - b.distance;
        });
        return pickResults;
    }
    addObjInfo(featureData, intersect, pickResult) {
        if (pickResult.intersection.object instanceof MapViewPoints_1.MapViewPoints) {
            pickResult.userData = featureData.objInfos[intersect.index];
            return;
        }
        else if (featureData.objInfos === undefined ||
            featureData.starts === undefined ||
            intersect.faceIndex === undefined) {
            return;
        }
        if (featureData.starts.length > 1) {
            let objInfosIndex = 0;
            for (const polygonStartFace of featureData.starts) {
                if (polygonStartFace > intersect.faceIndex) {
                    break;
                }
                objInfosIndex++;
            }
            pickResult.userData = featureData.objInfos[objInfosIndex - 1];
        }
        else {
            pickResult.userData = featureData.objInfos[0];
        }
    }
}
exports.PickHandler = PickHandler;
